import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { formatXOF } from "@/lib/api";
import { Star, Globe, Calendar, Check, ChevronLeft } from "lucide-react";

export default function AccompagnateurDetail() {
  const { id } = useParams();
  const [a, setA] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/accompagnateurs/${id}`);
        setA(data.accompagnateur);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) return <div className="container-jamm py-20 text-ink-soft">Chargement…</div>;
  if (!a) return <div className="container-jamm py-20 text-ink-soft">Accompagnateur introuvable.</div>;

  return (
    <div className="bg-bone">
      <div className="container-jamm pt-10 pb-4">
        <Link to="/accompagnateurs" className="inline-flex items-center text-ink-soft hover:text-sage-600 text-sm" data-testid="back-to-list">
          <ChevronLeft className="w-4 h-4" /> Retour aux accompagnateurs
        </Link>
      </div>

      <section className="container-jamm pb-16 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <div className="card-soft">
            <div className="flex items-start gap-5">
              <img src={a.avatar_url} alt={a.name} className="w-24 h-24 rounded-full object-cover ring-2 ring-sage-100" />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h1 className="font-display text-4xl text-ink">{a.name}</h1>
                  {a.verified && <span className="text-[10px] uppercase tracking-wider text-sage-700 bg-sage-50 px-2 py-1 rounded-full">Vérifié</span>}
                </div>
                <div className="text-ink-soft mt-1">{a.title}</div>
                <div className="flex items-center gap-3 mt-2 text-sm">
                  <span className="flex items-center gap-1"><Star className="w-4 h-4 text-gold-dark" fill="currentColor" /> <span className="font-medium text-ink">{a.rating}</span></span>
                  <span className="text-ink-mute">·</span>
                  <span className="text-ink-soft">{a.consultations} séances</span>
                  <span className="text-ink-mute">·</span>
                  <span className="text-ink-soft flex items-center gap-1"><Globe className="w-3.5 h-3.5" /> {a.country}</span>
                </div>
              </div>
            </div>

            <p className="text-ink-soft mt-6 leading-relaxed">{a.bio}</p>

            <div className="grid sm:grid-cols-2 gap-6 mt-8">
              <div>
                <div className="overline mb-3">Spécialités</div>
                <div className="flex flex-wrap gap-2">
                  {a.specialties?.map((s) => <span key={s} className="text-sm px-3 py-1 rounded-full bg-sage-50 text-sage-700 border border-sage-100">{s}</span>)}
                </div>
              </div>
              <div>
                <div className="overline mb-3">Langues</div>
                <div className="flex flex-wrap gap-2">
                  {a.languages?.map((l) => <span key={l} className="text-sm px-3 py-1 rounded-full bg-beige border border-border">{l}</span>)}
                </div>
              </div>
            </div>

            <div className="mt-8">
              <div className="overline mb-3">Disponibilités</div>
              <div className="flex flex-wrap gap-2">
                {(a.available_days || []).map((d) => <span key={d} className="text-xs uppercase tracking-wider px-3 py-1.5 rounded-full bg-white border border-sage-200 text-sage-700">{d}</span>)}
              </div>
            </div>
          </div>

          <div className="card-soft mt-6">
            <div className="overline">Engagements</div>
            <ul className="mt-4 space-y-3">
              {[
                "Écoute confidentielle, sans jugement.",
                "Disponibilité dans plusieurs fuseaux horaires.",
                "Possibilité de séance audio uniquement ou anonyme.",
              ].map((e) => (
                <li key={e} className="flex items-start gap-2 text-ink-soft text-[15px]"><Check className="w-4 h-4 text-sage-500 mt-0.5" />{e}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="card-soft sticky top-24">
            <div className="overline">Tarifs</div>
            <h2 className="font-display text-3xl text-ink mt-2">Choisissez votre durée</h2>
            <div className="mt-5 space-y-3">
              {[
                { d: 15, label: "Conversation courte", price: a.rate_15 },
                { d: 30, label: "Séance d'écoute", price: a.rate_30 },
                { d: 60, label: "Accompagnement profond", price: a.rate_60 },
              ].map((o) => (
                <div key={o.d} className="flex items-center justify-between rounded-xl border border-border bg-white px-4 py-3">
                  <div>
                    <div className="font-medium text-ink">{o.d} minutes</div>
                    <div className="text-xs text-ink-mute">{o.label}</div>
                  </div>
                  <div className="font-display text-2xl text-sage-700">{formatXOF(o.price)}</div>
                </div>
              ))}
            </div>
            <Link to={`/reservation/${a.id}`} className="btn-primary w-full justify-center mt-6" data-testid="acc-book-btn">
              <Calendar className="w-4 h-4" /> Prendre rendez-vous
            </Link>
            <div className="text-xs text-ink-mute text-center mt-4">Annulation gratuite jusqu'à 4h avant</div>
          </div>
        </div>
      </section>
    </div>
  );
}
